import * as React from 'react';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { useNotification } from './CustomNotification';
import {
  createOne as createOrder,
  validate as validateOrder,
  type Order,
} from '../data/orders';
import {
  searchByMobileNumber,
  createOne as createCustomer,
} from '../data/customers';
import OrderForm, {
  type FormFieldValue,
  type OrderFormState,
} from './OrderForm';
import PageContainer from './PageContainer';
import { useLanguage } from '../../contexts/LanguageContext';

const INITIAL_FORM_VALUES: Partial<OrderFormState['values']> = {
  customerName: '',
  customerNumber: '',
  destination: '',
  cost: undefined,
  people_number: undefined,
  price_per_person: undefined,
  full_amount: undefined,
};

export default function OrderCreate() {
  const navigate = useNavigate();
  const notifications = useNotification();
  const { t } = useLanguage();

  const [formState, setFormState] = React.useState<OrderFormState>(() => ({
    values: INITIAL_FORM_VALUES,
    errors: {},
  }));
  const formValues = formState.values;
  const formErrors = formState.errors;

  const setFormValues = React.useCallback(
    (newFormValues: Partial<OrderFormState['values']>) => {
      setFormState((previousState) => ({
        ...previousState,
        values: newFormValues,
      }));
    },
    [],
  );

  const setFormErrors = React.useCallback(
    (newFormErrors: Partial<OrderFormState['errors']>) => {
      setFormState((previousState) => ({
        ...previousState,
        errors: newFormErrors,
      }));
    },
    [],
  );

  const handleFormFieldChange = React.useCallback(
    (name: keyof OrderFormState['values'], value: FormFieldValue) => {
      const validateField = async (values: Partial<OrderFormState['values']>) => {
        const { issues } = validateOrder(values);
        const errorMessage = issues?.find((issue) => issue.path?.[0] === name)?.message;
        setFormErrors({
          ...formErrors,
          [name]: errorMessage ? t(errorMessage) : undefined,
        });
      };

      const newFormValues = { ...formValues, [name]: value };

      setFormValues(newFormValues);
      validateField(newFormValues);
    },
    [formValues, formErrors, setFormErrors, setFormValues, t],
  );

  const handleFormReset = React.useCallback(() => {
    setFormValues(INITIAL_FORM_VALUES);
  }, [setFormValues]);

  const handleFormSubmit = React.useCallback(async () => {
    const { issues } = validateOrder(formValues);
    if (issues && issues.length > 0) {
      setFormErrors(
        Object.fromEntries(issues.map((issue) => [issue.path?.[0], t(issue.message)])),
      );
      return;
    }
    setFormErrors({});

    try {
      let customerId: number;

      // Step 1: Search for customer by mobile number
      const existingCustomer = await searchByMobileNumber(formValues.customerNumber || '');

      if (existingCustomer) {
        // Step 2: Customer exists, use their ID
        customerId = existingCustomer.id;
      } else {
        // Step 3: Customer doesn't exist, create a new one
        const newCustomer = await createCustomer({
          name: formValues.customerName || '',
          mobile_number: formValues.customerNumber || '',
        } as any);
        customerId = newCustomer.id;
      }

      // Only send the required fields for the API.
      // cost and full_amount are optional and only included when provided.
      const orderData = {
        customer_id: customerId,
        customerName: formValues.customerName,
        customerNumber: formValues.customerNumber,
        destination: formValues.destination,
        people_number: formValues.people_number,
        ...(formValues.cost !== undefined && formValues.cost !== null && formValues.cost !== ''
          ? { cost: formValues.cost }
          : {}),
        ...(formValues.full_amount !== undefined && formValues.full_amount !== null && formValues.full_amount !== ''
          ? { full_amount: formValues.full_amount }
          : {}),
      };

      await createOrder(orderData as any);
      notifications.show(t('messages.createSuccess'), {
        severity: 'success',
        autoHideDuration: 3000,
      });

      navigate('/orders');
    } catch (createError) {
      notifications.show(
        `${t('messages.errorOccurred')}: ${(createError as Error).message}`,
        {
          severity: 'error',
          autoHideDuration: 3000,
        },
      );
      throw createError;
    }
  }, [formValues, navigate, notifications, setFormErrors, t]);

  const { direction } = useLanguage();
  const isRtl = direction === 'rtl';

  const handleBack = React.useCallback(() => {
    navigate('/orders');
  }, [navigate]);

  return (
    <PageContainer
      breadcrumbs={[{ title: t('orders.title'), path: '/orders' }, { title: t('orders.create') }]}
      backButton={
        <IconButton
          onClick={handleBack}
          sx={(theme) => ({
            backgroundColor: 'transparent',
            borderRadius: '50%',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : '#31394a',
              color: theme.palette.mode === 'dark'
                ? theme.palette.text.primary
                : 'white'
            },
          })}
          aria-label="back"
        >
          {isRtl ? <ArrowForward /> : <ArrowBackIcon />}
        </IconButton>
      }
    >
      <OrderForm
        formState={formState}
        onFieldChange={handleFormFieldChange}
        onSubmit={handleFormSubmit}
        onReset={handleFormReset}
        submitButtonLabel={t('common.create')}
      />
    </PageContainer>
  );
}
